import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gqlTagSwag from 'graphql-tag';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { PAGE_SIZE } from '../../../common/constants.tsx';
import {
	currentUserFragment,
	boardLocationFragment,
	epicFragment,
	estimationInfoFragment,
	timeTrackingOptionsFragment,
	epicLiteFragment,
	projectLocationFragment,
	issueParentTypeConfigFragment,
	boardFragment,
} from '../../../common/graphql/fragments/index.tsx';
import type {
	FullEpicDetails as FullEpicDetailsResponse,
	FullEpicDetailsVariables,
} from './__generated_apollo__/FullEpicDetails.ts';
import type { EpicQueryResponse, EpicQueryVariables } from './types.tsx';
import { transformEpic } from './utils.tsx';

export const EPIC_QUERY = gqlTagSwag`
    ${timeTrackingOptionsFragment}
    ${epicLiteFragment}
    ${epicFragment}
    ${estimationInfoFragment}
    ${projectLocationFragment}
    ${currentUserFragment}
    ${boardLocationFragment}
    ${issueParentTypeConfigFragment}
    ${boardFragment}
    query EpicPanelEpics($boardId: ID!, $isCompanyManaged: Boolean!, $skipEpicDetails: Boolean! = false) {
        jira @include(if: $isCompanyManaged) {
            timeTrackingOptions {
                ...TimeTrackingOptionsFragment
            }
        }
        boardScope(boardId: $boardId, isCMP: $isCompanyManaged) {
            issueParents @skip(if: $skipEpicDetails) {
                ...EpicPanelIssueParentFragment
                estimationInfo @include(if: $isCompanyManaged) {
                    ...EstimationInfoFragment
                }
            }
            issueParents @include(if: $skipEpicDetails) {
                ...EpicPanelIssueParentLiteFragment
            }
            projectLocation @skip(if: $isCompanyManaged) {
                ...ProjectLocationFragment
            }
            currentUser {
                ...CurrentUserFragment
            }
            boardLocation @include(if: $isCompanyManaged) {
                ...BoardLocationFragment
            }
            board @include(if: $isCompanyManaged) {
                ...BoardFragment
            }
        }
        globalConfig @include(if: $isCompanyManaged) {
            issueParentTypeConfig {
                ...IssueParentTypeConfigFragment
            }
        }
    }
`;

/**
 * @deprecated use `useEpicsFilterData` from '@atlassian/jira-backlog-epics-store' instead.
 * Should be removed with 'jsw_epic_panel_paginated_fetch' cleanup
 */
export const useEpicQuery = (boardId: number, isCompanyManaged: boolean) => {
	const [hasFullFetched, setFullFetched] = useState(false);
	const [shouldShowLoadingState, setShouldShowLoadingState] = useState(false);

	const { data, loading, error, refetch } = useQuery<EpicQueryResponse, EpicQueryVariables>(
		EPIC_QUERY,
		{
			skip: fg('jsw_epic_panel_paginated_fetch') || __SERVER__,
			context: {
				headers: {
					'is-company-managed-board': isCompanyManaged,
				},
			},
			variables: {
				boardId,
				isCompanyManaged,
				skipEpicDetails: ff('jsw-epic-panel-performance-async-epic-details'),
			},
			fetchPolicy: 'cache-first',
		},
	);

	const fetchFull = useCallback(
		async ({ triggerLoadingState = false } = {}) => {
			if (ff('jsw-epic-panel-performance-async-epic-details')) {
				setShouldShowLoadingState(triggerLoadingState);
				setFullFetched(true);

				const result = await refetch({
					boardId,
					isCompanyManaged,
					skipEpicDetails: false,
				});

				setShouldShowLoadingState(false);
				return result;
			}

			return refetch();
		},
		[boardId, isCompanyManaged, refetch],
	);

	const transformedData = useMemo(
		() =>
			transformEpic({
				isCompanyManaged,
				isLoading: loading && shouldShowLoadingState,
				hasError: !!error,
				data,
			}),
		[data, error, isCompanyManaged, loading, shouldShowLoadingState],
	);

	return {
		data: transformedData,
		loading,
		error,
		refetch: fetchFull,
		hasFullFetched: ff('jsw-epic-panel-performance-async-epic-details')
			? hasFullFetched
			: undefined,
	};
};

/* -------------------------------------------------------------------------- */

/**
 * Query to fetch full epic details for the epic panel. This is responsible for updating the apollo cache,
 * and the values are read via VIEW_EPIC_DETAILS_QUERY.
 */
const EPIC_DETAILS_QUERY = gqlTagSwag`
    ${epicFragment}
    ${estimationInfoFragment}
    query FullEpicDetails($boardId: ID!, $isCompanyManaged: Boolean!, $pageSize: Int!, $startCursor: String) {
        boardScope(boardId: $boardId, isCMP: $isCompanyManaged) {
            parents(first: $pageSize, after: $startCursor) {
                pageInfo {
                    hasNextPage
                    endCursor
                }
                edges {
                    node {
                        ...EpicPanelIssueParentFragment
                        estimationInfo @include(if: $isCompanyManaged) {
                            ...EstimationInfoFragment
                        }
                    }
                }
            }
        }
    }
`;

export const useEpicDetailsQuery = ({
	boardId,
	isCompanyManaged,
}: {
	boardId: number;
	isCompanyManaged: boolean;
}) =>
	useQuery<FullEpicDetailsResponse, FullEpicDetailsVariables>(EPIC_DETAILS_QUERY, {
		notifyOnNetworkStatusChange: true,
		context: {
			headers: {
				'is-company-managed-board': isCompanyManaged,
			},
		},
		variables: {
			boardId: String(boardId),
			isCompanyManaged,
			pageSize: PAGE_SIZE,
		},
		fetchPolicy: 'network-only',
		skip: true,
	});
